import { ReactNode } from 'react';
import { Navigate, To } from 'react-router';
import LoadingScreen from '~/components/LoadingScreen';
import { useIsOnlySwan } from '~/hooks/useIsOnlySwan';
import { RouteConfig } from '~/routes/routeConfig';

interface Props {
  children: ReactNode;
  redirectTo?: To;
}

/**
 * Guard that excludes users who only have access to the Sibill/Swan account (card-only collaborators).
 * Such users are redirected away from account-wide pages they must not see (e.g. Automatic reconciliation).
 *
 * While the Swan-only status is still resolving, a loading screen is shown so the protected children are
 * not mounted (and their requests not fired) before the redirect decision can be made.
 *
 * @param children - The content to render when the user is not a Swan-only collaborator
 * @param redirectTo - Optional redirect path (defaults to app root)
 * @constructor
 */
const ExcludeSwanOnlyGuard: React.FC<Props> = ({ children, redirectTo }) => {
  const { isOnlySwan, isResolving } = useIsOnlySwan();

  if (isResolving) {
    return <LoadingScreen />;
  }

  if (isOnlySwan) {
    return <Navigate replace to={redirectTo ?? RouteConfig.AppRoot.buildLink()} />;
  }

  return <>{children}</>;
};

export default ExcludeSwanOnlyGuard;
